import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import queryString from 'query-string';
import { Redirect } from 'react-router-dom';
import { Formik, Form } from 'formik';
import lodashCompact from 'lodash/fp/compact';
import lodashGroupBy from 'lodash/fp/groupBy';

import { getFloorPlanDisplayFields } from 'common/apartmentHelpers';

import { formatPrice } from 'common/formatters';

import { SR_API_ACTION_APARTMENT_TOURS_SUBMIT_TOUR_FEEDBACK } from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_APARTMENTS,
  DATA_TABLE_VIEW_ID_APARTMENT_BY_ID,
} from 'common/dataTableConstants';

import { createFormValidationFn } from 'client/helpers/formikHelpers';

import { dataTable } from 'client/helpers/dataTables';

import { SCREEN_ID_LOCATOR_TOURS_LOCKBOX_CODE_PAGE } from 'client/constants/screenIds';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import postWithState from 'client/actions/postWithState';

import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrContactUsButton from 'client/components/SrContactUsButton';
import SrLocatorToursNavbar from 'client/components/SrLocatorToursNavbar';
import SrFlex from 'client/components/SrFlex';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import ButtonDeprecated, {
  BUTTON_THEME_YELLOW_GREYCLIFF,
} from 'client/components/ButtonDeprecated';

const getFormInitialValues = ({ tourId, floorPlans }) => {
  return {
    tourId: tourId,
    renterFeedback: floorPlans.map((floorPlan) => ({
      listingId: floorPlan.showUnits[0].id,
      positiveFeedback: '',
      negativeFeedback: '',
      questions: '',
      rating: null,
    })),
  };
};

const getFloorPlanFormFields = ({ index }) => {
  return lodashCompact([
    index === 0 && {
      id: 'tourId',
      type: 'hidden',
    },
    {
      id: `renterFeedback[${index}].listingId`,
      type: 'hidden',
    },
    {
      id: `renterFeedback[${index}].positiveFeedback`,
      title: "Renter's positive feedback",
      type: 'long_text',
      properties: {
        placeholder: 'Write here...',
      },
    },
    {
      id: `renterFeedback[${index}].negativeFeedback`,
      title: "Renter's negative feedback",
      type: 'long_text',
      properties: {
        placeholder: 'Write here...',
      },
    },
    {
      id: `renterFeedback[${index}].renterQuestions`,
      title: "Renter's questions for us?",
      type: 'long_text',
      properties: {
        placeholder: 'Example: Is the landlord fine with their 65lb dog?',
      },
    },
    {
      id: `renterFeedback[${index}].rating`,
      title: 'Rating (5 stars = they love it)',
      type: 'star_rating',
      validations: ['required'],
    },
  ]);
};

// written note or star rating required!!
const getFormFields = ({ floorPlans }) => {
  return [
    ...floorPlans.reduce((formFields, listing, index) => {
      formFields.push(...getFloorPlanFormFields({ index }));
      return formFields;
    }, []),
  ];
};

function getDataTableSettings({ apartmentId }) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_APARTMENTS,
    viewId: [DATA_TABLE_VIEW_ID_APARTMENT_BY_ID, apartmentId].join(':'),
    count: 1,
    filter: {
      'apartments.id': parseInt(apartmentId, 10),
    },
    itemsPropName: 'apartments',
  };
  return dataTableSettings;
}

@withTransitionHook
@withScreenId(SCREEN_ID_LOCATOR_TOURS_LOCKBOX_CODE_PAGE)
@dataTable(({ match }) => {
  return getDataTableSettings({
    apartmentId: match.params.apartmentId,
  });
})
@branch({
  guestCardFloorPlanIds: ['locatorTours', 'guestCardFloorPlanIds'],
})
@withScrollToTop
class LocatorToursLockboxCodePage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Locator Tours',
  });

  static propTypes = {
    guestCardFloorPlanIds: PropTypes.array.isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  _onRenterFeedbackSubmit = (values) => {
    const { history, dispatch, match } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_APARTMENT_TOURS_SUBMIT_TOUR_FEEDBACK,
      payload: {
        tourId: values.tourId,
        renterFeedback: values.renterFeedback,
      },
      onSuccess() {
        const apartmentId = parseInt(match.params.apartmentId, 10);
        history.push(`/locatortours/${apartmentId}/success`);
      },
    });
  };

  render() {
    const { apartments, guestCardFloorPlanIds, match, location } = this.props;
    const [apartment] = apartments;

    const locationQuery = queryString.parse(location.search);
    const tourId = parseInt(locationQuery.tourId, 10);

    if (!tourId || !apartment || guestCardFloorPlanIds.length === 0) {
      return <Redirect to={`/locatortours/${match.params.apartmentId}`} />;
    }

    const unitsByFloorPlanId = lodashGroupBy('floorPlanId', apartment.listings);
    const floorPlans = apartment.floorPlans
      .filter(({ id }) => guestCardFloorPlanIds.includes(id))
      .map((floorPlan) => getFloorPlanDisplayFields(floorPlan, unitsByFloorPlanId[floorPlan.id]));

    const formFields = getFormFields({ floorPlans });

    return (
      <div>
        <SrLocatorToursNavbar
          withLogo
          redirectTo="/locatortours"
          withTopPadding
          cornerNode={<SrContactUsButton />}
        />
        <SrBox textAlign="center">
          <SrText font="greycliff" size="40" weight="bolder" leading="45">
            Combo Lockbox
            <br />
            Access Code
          </SrText>
        </SrBox>
        <SrBox
          backgroundColor="#FFF7D7"
          borderRadius="14px"
          padding="35px 30px 30px"
          margin="27px 40px 40px"
          textAlign="center"
        >
          <SrText font="greycliff" size="80" weight="bolder" leading="45">
            {apartment.lockboxCode}
          </SrText>
        </SrBox>
        {apartment.lockboxPhotoUrl && (
          <SrBox width="100%" padding="0 40px" className="border-box">
            <img
              width="100%"
              height="100%"
              alt={apartment.lockboxPhotoUrl}
              src={apartment.lockboxPhotoUrl}
              style={{ borderRadius: '7px' }}
              className="dropshadow-bottom-edge-large"
            />
          </SrBox>
        )}
        {apartment.lockboxInstructions && (
          <SrBox textAlign="center" padding="18px 30px 57px">
            <SrBox paddingBottom="7px">
              <SrText font="greycliff" size="xl" weight="bolder">
                Lockbox Location
              </SrText>
            </SrBox>
            <SrText whiteSpace="prewrap" font="greycliff" size="large" weight="medium">
              {apartment.lockboxInstructions}
            </SrText>
          </SrBox>
        )}
        <SrBox paddingBottom="130px">
          <SrBox paddingBottom="4px">
            <SrText font="greycliff" size="xl" weight="bolder" paddingLeft="25px">
              Write Renter Feedback
            </SrText>
          </SrBox>

          <SrBox margin="0 10px 150px">
            <Formik
              onSubmit={this._onRenterFeedbackSubmit}
              initialValues={getFormInitialValues({ tourId, floorPlans })}
              validate={createFormValidationFn(formFields)}
              render={(formikBag) => (
                <Form>
                  {floorPlans.map((floorPlan, index) => {
                    const [listing] = floorPlan.showUnits;
                    return (
                      <SrBox
                        key={floorPlan.id}
                        padding="15px"
                        marginTop="10px"
                        boxShadow="0px 9px 18px rgba(25, 51, 64, 0.07)"
                        borderRadius="14px"
                      >
                        <SrFlex justify="between">
                          <SrFlex>
                            <img
                              width="80"
                              height="75"
                              alt={listing.sunroomPhotoUrls[0]}
                              src={listing.sunroomPhotoUrls[0]}
                              style={{ borderRadius: '7px', marginRight: '10px' }}
                              className="dropshadow-bottom-edge"
                            />
                            <SrFlex justify="between" direction="col">
                              <SrText
                                display="block"
                                weight="bolder"
                                size="larger"
                                font="greycliff"
                              >
                                {listing.apartmentUnit}
                              </SrText>
                              <SrText
                                display="block"
                                weight="medium"
                                size="medium"
                                font="greycliff"
                                spacing="41"
                              >
                                {[
                                  pluralize('Bed', floorPlan.bedrooms, true),
                                  pluralize('Bath', floorPlan.bathrooms, true),
                                  `${floorPlan.squareFeet} ft.`,
                                ].map((text) => (
                                  <SrBox key={text} display="inlineBlock" paddingRight="10px">
                                    {text}
                                  </SrBox>
                                ))}
                              </SrText>
                              <SrText color="grey600" weight="medium">
                                {floorPlan.otherUnits.length === 0 ? (
                                  <span>&nbsp;</span>
                                ) : (
                                  `Same floorplan as: ${floorPlan.otherUnits.map(
                                    ({ apartmentUnit }) => apartmentUnit,
                                  )}`
                                )}
                              </SrText>
                            </SrFlex>
                          </SrFlex>
                          <SrText color="green" size="larger" weight="medium">
                            {formatPrice(listing.price)}
                          </SrText>
                        </SrFlex>
                        {getFloorPlanFormFields({ index }).map((formField) => (
                          <SrFormikField
                            key={formField.id}
                            formField={formField}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        ))}
                      </SrBox>
                    );
                  })}
                  <SrBox
                    position="fixed"
                    bottom="0"
                    left="0"
                    width="100%"
                    boxShadow="0px -6px 17px rgba(219, 176, 0, 0.14)"
                  >
                    <SrBox
                      width="100%"
                      margin="0 auto"
                      paddingBottom="43px"
                      paddingTop="7px"
                      className="greyBlur-90 border-box"
                      paddingRight="10px"
                      paddingLeft="10px"
                    >
                      <ButtonDeprecated
                        theme={BUTTON_THEME_YELLOW_GREYCLIFF}
                        disabled={!formikBag.isValid}
                        type="submit"
                      >
                        Submit Renter Feedback
                      </ButtonDeprecated>
                    </SrBox>
                  </SrBox>
                </Form>
              )}
            />
          </SrBox>
        </SrBox>
      </div>
    );
  }
}

LocatorToursLockboxCodePage.displayName = 'LocatorToursLockboxCodePage';

export default LocatorToursLockboxCodePage;
